import {useTranslation} from 'react-i18next';
import {MoreVertical} from 'lucide-react';
import type {ShortGuest} from '@chekin/core';
import {
  ActionRow,
  Avatar,
  Button,
  IconButton,
  ResponsiveDropdown,
  Tag,
} from '@chekinapp/ui';
import {useGuestRowActions, type GuestRowActions} from '@chekin/shared/guest-form';
import type {GuestRowState} from 'utils/guestRoster';
import {getInitials} from 'utils/common';

type FilledState = Exclude<GuestRowState, 'empty'>;

const ROW_COPY: Record<
  FilledState,
  {chip: string; chipShort?: string; action?: string; actionShort?: string}
> = {
  registered: {chip: 'status_registered'},
  verify: {
    chip: 'status_iv_pending',
    chipShort: 'status_iv_pending_short',
    action: 'status_verify_id',
  },
  details: {
    chip: 'status_details_pending',
    action: 'complete_details',
    actionShort: 'complete_details_short',
  },
};

function ShortLabel({full, short}: {full: string; short?: string}) {
  if (!short) return <>{full}</>;

  return (
    <>
      <span className="[@media(max-width:389px)]:hidden">{full}</span>
      <span className="[@media(min-width:390px)]:hidden">{short}</span>
    </>
  );
}

type Props = GuestRowActions & {
  guest: ShortGuest;
  isLeader?: boolean;
};

function GuestRosterRow({guest, isLeader, ...rowActions}: Props) {
  const {t} = useTranslation();
  const {state, goToEdit, dropdownOptions, showActions, dialogs} = useGuestRowActions({
    guest,
    isLeader,
    ...rowActions,
  });

  if (state === 'empty') return null;

  const copy = ROW_COPY[state];
  const isPending = state !== 'registered';
  const name = guest.full_name || t('unknown_guest');

  return (
    <>
      <ActionRow
        data-roster-row=""
        interactive={false}
        hideChevron
        leading={
          <Avatar
            shape="square"
            tone={isPending ? 'warning' : 'success'}
            fallback={getInitials(name)}
            className="size-[var(--roster-avatar)] text-[length:var(--roster-avatar-font)] font-bold [&_*]:font-bold"
          />
        }
        title={<span className="block truncate leading-[18px]">{name}</span>}
        description={
          <Tag
            tone={isPending ? 'warning' : 'success'}
            fill="solid"
            size="xs"
            className="mt-[var(--roster-chip-mt)] w-fit font-semibold"
          >
            <ShortLabel
              full={t(copy.chip)}
              short={copy.chipShort ? t(copy.chipShort) : undefined}
            />
          </Tag>
        }
        trailing={
          <span className="flex items-center gap-1">
            {copy.action && (
              <Button
                size="sm"
                onClick={goToEdit}
                className="h-auto min-h-[var(--roster-btn-h)] rounded-xl px-[var(--roster-btn-px)] text-[length:var(--roster-btn-fs)] font-semibold"
              >
                <ShortLabel
                  full={t(copy.action)}
                  short={copy.actionShort ? t(copy.actionShort) : undefined}
                />
              </Button>
            )}
            {showActions && (
              <ResponsiveDropdown
                side="bottom"
                align="end"
                options={dropdownOptions}
                trigger={
                  <IconButton
                    variant="ghost"
                    label={t('guest_actions')}
                    className="h-9 w-8 rounded-[10px] !text-[var(--chekin-color-gray-2)] hover:bg-[var(--chekin-neutral-100)] hover:!text-[var(--chekin-color-brand-navy)]"
                    icon={<MoreVertical className="size-5" />}
                  />
                }
              />
            )}
          </span>
        }
      />
      {dialogs}
    </>
  );
}

export {GuestRosterRow};
